import {
  Lead,
  GuideSection,
  P,
  UL,
  OL,
  LI,
  Term,
  GuideLink,
  Callout,
  GuideTable,
} from './guide-ui';

/** Body for "What is log management?", pillar for the Log management cluster. */
export function WhatIsLogManagementGuideBody() {
  return (
    <>
      <Lead>
        Log management is the practice of collecting, storing, searching and
        retaining the logs your systems produce, so you can troubleshoot issues,
        monitor health, investigate security incidents, and meet compliance
        requirements. It is the foundation that observability and SIEM are built on.
        This guide explains what log management is, how it works, and how it relates
        to the categories around it.
      </Lead>

      <GuideSection id="what-is-it" title="What is log management?">
        <P>
          <Term>Log management</Term> is the end-to-end handling of log data:{' '}
          collecting logs from across your estate, parsing them into a usable shape,
          storing them efficiently, making them searchable, and retaining them for as
          long as you need. A log is a timestamped record of something that happened:
          a request served, an error thrown, a user signing in, a config changing.
          Multiply that across every server, container, application and cloud service
          and you have a firehose that only delivers value if it is centralised and
          queryable.
        </P>
      </GuideSection>

      <GuideSection id="what-it-does" title="What log management does">
        <UL>
          <LI><Term>Collection</Term>: gather logs from hosts, containers, apps, network gear and cloud services, usually via an agent or API.</LI>
          <LI><Term>Parsing &amp; normalisation</Term>: turn varied formats into structured, consistent fields you can query.</LI>
          <LI><Term>Storage</Term>: keep the data in a cost-efficient store built for the volume.</LI>
          <LI><Term>Search &amp; analysis</Term>: query across everything quickly to answer a question or trace an incident.</LI>
          <LI><Term>Alerting</Term>: notify when patterns or thresholds indicate a problem.</LI>
          <LI><Term>Retention &amp; archival</Term>: keep logs for the window your operations and regulations require.</LI>
        </UL>
      </GuideSection>

      <GuideSection id="how-it-works" title="How a log management pipeline works">
        <OL>
          <LI><Term>Ship</Term>: an agent or SDK forwards logs from the source.</LI>
          <LI><Term>Parse &amp; enrich</Term>: a pipeline structures the data and adds context (service, environment, geo-IP).</LI>
          <LI><Term>Store</Term>: events land in an indexed or columnar store optimised for query.</LI>
          <LI><Term>Search &amp; visualise</Term>: engineers query, build dashboards, and set alerts.</LI>
          <LI><Term>Retain or expire</Term>: data ages into cheaper tiers or is dropped per policy.</LI>
        </OL>
      </GuideSection>

      <GuideSection id="vs-others" title="Log management vs. SIEM vs. observability">
        <GuideTable
          head={['Category', 'Primary goal', 'Audience']}
          rows={[
            ['Log management', 'Collect, store, search and retain logs', 'Everyone (ops, dev, security)'],
            ['Observability', 'Understand system health & performance (logs, metrics, traces)', 'SRE / platform / dev'],
            ['SIEM', 'Detect, investigate and respond to security threats', 'Security / SOC'],
          ]}
        />
        <P>
          They share the same raw material, logs, and increasingly the same engine.
          Log management is the base; observability and{' '}
          <GuideLink href="/guides/what-is-siem">SIEM</GuideLink> are specialised
          layers on top. See also{' '}
          <GuideLink href="/guides/siem-vs-log-management-vs-xdr">SIEM vs log management vs XDR</GuideLink>.
        </P>
      </GuideSection>

      <GuideSection id="challenges" title="Common challenges">
        <UL>
          <LI><Term>Cost at volume</Term>: per-GB pricing and storage-heavy engines make logging expensive, pushing teams to drop data they later need.</LI>
          <LI><Term>Search speed</Term>: querying billions of events slowly makes investigation painful.</LI>
          <LI><Term>Inconsistent formats</Term>: unstructured logs are hard to parse and query, so see <GuideLink href="/guides/structured-logging">structured logging</GuideLink>.</LI>
          <LI><Term>Retention vs. budget</Term>: compliance wants long retention; cost wants short, so see <GuideLink href="/guides/log-retention-requirements">log retention requirements</GuideLink>.</LI>
        </UL>
      </GuideSection>

      <GuideSection id="how-logpulse-helps" title="How LogPulse approaches log management">
        <P>
          LogPulse is log management built on <Term>ClickHouse</Term> for columnar
          speed and compression, with a familiar pipe-based query language (LPQL) and
          natural-language AI search on top, visual pipelines for parsing and
          redaction, and flat pricing instead of per-GB. The same engine also powers
          service intelligence and a SIEM, so you do not run separate, separately-
          priced stacks.
        </P>
        <Callout title="One base, many layers">
          <p>
            See <GuideLink href="/clickhouse-log-management">log management on ClickHouse</GuideLink>{' '}
            for the engine, and{' '}
            <GuideLink href="/ai-log-search">AI log search</GuideLink> for querying in
            natural language.
          </p>
        </Callout>
      </GuideSection>
    </>
  );
}
